/** Em que família do esquema IFC cai cada entidade.
 *
 *  Vivia dentro do EntitiesBrowser. Passou para aqui quando a pesquisa
 *  global precisou da mesma resposta: sem isto, procurar "wall" trazia
 *  o Pset_WallCommon à frente das paredes, e um property set nem
 *  sequer se consegue seleccionar no 3D.
 *
 *  Uma definição só, usada pelos dois.
 */
import { getInheritance } from '@viewer/lib/ifcInheritance'

const ELEMENT_SUFFIX_RE =
  /(SEGMENT|FITTING|TERMINAL|DEVICE|CONTROLLER|APPLIANCE|FIXTURE|FASTENER|ACCESSORY|REINFORCINGBAR|REINFORCINGMESH|TENDON|TENDONANCHOR|VALVE|PUMP|FAN|COIL|BOILER|CHILLER|DAMPER|SILENCER|SENSOR|ACTUATOR|ALARM|UNITARYEQUIPMENT|TRANSFORMER|SWITCHINGDEVICE|PROTECTIVEDEVICE|OUTLET|LAMP|LUMINAIRE|BEARING|VIBRATIONISOLATOR)$/


// Flat browser of every IFC entity present in the active model.
// Following IFC schema vocabulary: an "entity" is the top-level
// EXPRESS construct (IfcWall, IfcSlab, IfcRelAssignsToGroup, ...).
// We DO NOT use the word "category" or "class" anywhere user-facing:
// the IFC schema reserves those for IfcClassification (external
// classification systems) and for the EXPRESS notion of class
// (which is the same as entity for our purposes).
//
// Entities are bucketed into IFC family groups so the user sees the
// schema partition rather than a 200-item flat list:
//
//   IfcSpatialStructureElement   - spatial hierarchy nodes
//   IfcElement                   - physical building elements
//   IfcGroup (and subtypes)      - logical aggregations
//   IfcTypeObject (IfcXxxType)   - type definitions shared by instances
//   IfcRelationship              - Ifc*Rel* objectified relations
//   IfcPropertyDefinition        - property/quantity sets
//   Outros                       - anything else (IfcGrid, IfcAnnotation, …)
export type Family =
  | 'spatial'
  | 'element'
  | 'group'
  | 'type'
  | 'relationship'
  | 'propertyDef'
  | 'resource'
  | 'other'

const SPATIAL_ENTITIES = new Set([
  'IFCPROJECT',
  'IFCSITE',
  'IFCBUILDING',
  'IFCBUILDINGSTOREY',
  'IFCSPACE',
  'IFCSPATIALZONE',
])

const GROUP_ENTITIES = new Set([
  'IFCGROUP',
  'IFCSYSTEM',
  'IFCDISTRIBUTIONSYSTEM',
  'IFCBUILDINGSYSTEM',
  'IFCSTRUCTURALANALYSISMODEL',
  'IFCZONE',
  'IFCELEMENTASSEMBLY',
])

// Camada de RECURSOS do esquema (não são IfcRoot, não são elementos):
// materiais, unidades, perfis, estilos de apresentação, atores.
const RESOURCE_PREFIXES = [
  'IFCMATERIAL', // IfcMaterial, ...Layer(Set)(Usage), ...Profile(Set)(Usage)
  'IFCUNIT', // IfcUnitAssignment
  'IFCPRESENTATION',
  'IFCSURFACESTYLE',
  'IFCCURVESTYLE',
  'IFCFILLAREASTYLE',
  'IFCTEXTSTYLE',
  'IFCCOLOUR',
  'IFCCLASSIFICATION',
  'IFCLIBRARY',
  'IFCDOCUMENT',
  'IFCAPPLICATION',
  'IFCOWNERHISTORY',
  'IFCPERSON',
  'IFCORGANIZATION',
  'IFCADDRESS',
  'IFCPOSTALADDRESS',
  'IFCTELECOMADDRESS',
  'IFCACTORROLE',
]
const RESOURCE_EXACT = new Set([
  'IFCSIUNIT',
  'IFCCONVERSIONBASEDUNIT',
  'IFCDERIVEDUNIT',
  'IFCMONETARYUNIT',
  'IFCPROJECTEDCRS',
  'IFCMAPCONVERSION',
])

export function classify(entity: string): Family {
  const u = entity.toUpperCase()
  if (SPATIAL_ENTITIES.has(u)) return 'spatial'
  if (GROUP_ENTITIES.has(u)) return 'group'
  if (RESOURCE_EXACT.has(u) || u.endsWith('PROFILEDEF')) return 'resource'
  if (RESOURCE_PREFIXES.some((p) => u.startsWith(p))) return 'resource'
  if (u.endsWith('TYPE')) return 'type'
  if (u.startsWith('IFCREL')) return 'relationship'
  if (
    u.startsWith('IFCPROPERTY') ||
    u.startsWith('IFCQUANTITY') ||
    u === 'IFCELEMENTQUANTITY' ||
    u === 'IFCPHYSICALCOMPLEXQUANTITY' ||
    u === 'IFCPROPERTYSETTEMPLATE'
  ) {
    return 'propertyDef'
  }
  if (u === 'IFCGRID' || u === 'IFCGRIDAXIS' || u === 'IFCANNOTATION') {
    return 'other'
  }
  // Só é "elemento físico" quem herda mesmo de IfcElement, segundo a
  // tabela de herança IFC, mais os sufixos das famílias MEP/estrutura
  // que a tabela não cobre uma a uma. O resto cai em "Outros"
  // (honesto) em vez de inflacionar os elementos.
  const chain = getInheritance(u)
  if (chain.includes('IfcElement') || ELEMENT_SUFFIX_RE.test(u)) {
    return 'element'
  }
  return 'other'
}

/** As famílias que valem a pena numa pesquisa de elementos.
 *
 *  Um property set, uma relação objectivada ou uma definição de tipo
 *  não têm geometria e não se seleccionam: aparecerem nos resultados
 *  só serve para enterrar o que interessa. Os espaciais ficam porque
 *  procurar "Piso 1" é um uso legítimo. */
const FAMILIAS_PESQUISAVEIS = new Set<Family>(['element', 'spatial', 'group'])

export function ehPesquisavel(entity: string): boolean {
  return FAMILIAS_PESQUISAVEIS.has(classify(entity))
}
